import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Tabs, DatePicker } from "antd";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isoWeek from "dayjs/plugin/isoWeek";
import ProductList from "./productlist";
import ProductVariationStatus from "./components/ProductVariationStatus";
import Orders from "./ProductOrdersList";
import OrdersSummary from "./productOrdersSummary";
import DeliverySummary from "./productDeliverySummary";
import StockEvolution from "./productStockEvolution";
import StockData from "./components/StockData";
import RefactoredEditProduct from "./components/RefactoredEditProduct";
import DynamicTab, { createTabConfig } from "./DynamicTab";
import { getProfileTest } from "../../core/api/auth";

dayjs.extend(minMax);
dayjs.extend(isSameOrAfter);
dayjs.extend(isoWeek);

const ProductDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const pathSegment = location.pathname.split("/").pop();

  const [dateRange, setDateRange] = useState([dayjs(), dayjs().add(365, "day")]);
  const [dynamicTabs, setDynamicTabs] = useState([]);
  const [activeKey, setActiveKey] = useState("list");
  const productListRef = useRef(null);
  const selectedClients = useSelector((state) => state.selectedClients);

  // Track if we've already processed the initial URL
  const initialUrlProcessedRef = useRef(false);

  useEffect(() => {
    // Only process the URL once on initial load to prevent duplicate tabs
    if (initialUrlProcessedRef.current) return;

    // Check URL path to determine what to display
    const pathParts = location.pathname.split("/");
    const basePath = pathParts[2]; // products/[basePath]

    // Handle base tabs
    const validPaths = [
      "list",
      "variation-status",
      "orders",
      "orders-summary",
      "delivery-summary",
      "stock-evolution",
      "stocks",
    ];

    if (validPaths.includes(basePath)) {
      setActiveKey(basePath);
      initialUrlProcessedRef.current = true;
      return;
    }

    // Handle dynamic tabs
    if (basePath === "create") {
      // Create new product tab
      const clientId = pathParts[3] || null;
      // Add the new tab with a slight delay to ensure proper rendering
      setTimeout(() => {
        addDynamicTab("add-product", { clientId, title: "Add Product" }, true);
      }, 0);
      initialUrlProcessedRef.current = true;
      return;
    }

    if (basePath === "edit") {
      // Edit product tab
      const clientId = pathParts[3];
      const productId = pathParts[4];
      const variationId = pathParts[5] || null;
      const warehouseId = pathParts[6] || null;

      if (clientId && productId) {
        // Add the new tab with a slight delay to ensure proper rendering
        setTimeout(() => {
          addDynamicTab(
            "edit-product",
            {
              clientId,
              productId,
              variationId,
              warehouseId,
              title: "Edit Product",
            },
            true
          );
        }, 0);
        initialUrlProcessedRef.current = true;
      }
      return;
    }

    if (basePath === "view") {
      // View product tab
      const clientId = pathParts[3];
      const productId = pathParts[4];
      const variationId = pathParts[5] || null;
      const warehouseId = pathParts[6] || null;

      if (clientId && productId) {
        // Add the new tab with a slight delay to ensure proper rendering
        setTimeout(() => {
          addDynamicTab(
            "view-product",
            {
              clientId,
              productId,
              variationId,
              warehouseId,
              title: "View Product",
            },
            true
          );
        }, 0);
        initialUrlProcessedRef.current = true;
      }
      return;
    }

    // Default to list if no match
    if (!activeKey || activeKey === "") {
      setActiveKey("list");
      navigate("/products/list");
    }

    initialUrlProcessedRef.current = true;
  }, [location.pathname]);


  // Permission check

  const addDynamicTab = useCallback(
    (tabType, options = {}, fromUrl = false) => {
      const tabConfig = createTabConfig(tabType, options);
      const parentTab = activeKey;

      // Create the tab object
      const newTab = {
        key: tabConfig.key,
        label: (
          <span>
            {tabConfig.title}
            <span
              className="ms-2"
              style={{ cursor: "pointer", fontSize: "14px" }}
              onClick={(e) => {
                e.stopPropagation();
                removeTab(tabConfig.key, parentTab);
              }}>
              ×
            </span>
          </span>
        ),
        children: (
          <DynamicTab
            tabData={tabConfig.data}
            onSuccess={() => {
              removeTab(tabConfig.key, parentTab);
              if (productListRef.current && productListRef.current.fetchProducts) {
                productListRef.current.fetchProducts(1, 5000);
              }
            }}
            onCancel={() => {
              removeTab(tabConfig.key, parentTab);
            }}
            onClose={() => {
              removeTab(tabConfig.key, parentTab);
            }}
          />
        ),
        closable: true,
      };

      // Important: Use a function to update state to ensure we have the latest state
      setDynamicTabs((prev) => [...prev, newTab]);

      // Set active key with a slight delay to ensure the tab is properly added to the DOM
      setTimeout(() => {
        setActiveKey(tabConfig.key);
      }, 0);

      // Update URL based on tab type, but only if not coming from URL
      if (!fromUrl) {
        updateUrlForTab(tabType, options);
      }
    },
    [activeKey, navigate]
  );

  const updateUrlForTab = useCallback(
    (tabType, options = {}) => {
      switch (tabType) {
        case "add-product":
          navigate(`/products/create${options.clientId ? `/${options.clientId}` : ""}`);
          break;
        case "edit-product":
          navigate(
            `/products/edit/${options.clientId}/${options.productId}${
              options.variationId ? `/${options.variationId}` : ""
            }${options.warehouseId ? `/${options.warehouseId}` : ""}`
          );
          break;
        case "view-product":
          navigate(
            `/products/view/${options.clientId}/${options.productId}${
              options.variationId ? `/${options.variationId}` : ""
            }${options.warehouseId ? `/${options.warehouseId}` : ""}`
          );
          break;
        default:
          break;
      }
    },
    [navigate]
  );

  const removeTab = (targetKey, parentTab = "list") => {
    setDynamicTabs((prev) => prev.filter((tab) => tab.key !== targetKey));
    setActiveKey(parentTab);
    navigate(`/products/${parentTab}`);
  };

  // Base tabs
  const baseTabs = [
    {
      key: "list",
      label: "Products",
      children: <ProductList addDynamicTab={addDynamicTab} ref={productListRef} />,
    },
    {
      key: "variation-status",
      label: "Variation Status",
      children: <ProductVariationStatus addDynamicTab={addDynamicTab} />,
    },
    {
      key: "orders",
      label: "Orders",
      children: <Orders dateRange={dateRange} />,
    },
    {
      key: "orders-summary",
      label: "Orders Summary",
      children: <OrdersSummary />,
    },
    {
      key: "delivery-summary",
      label: "Delivery Summary",
      children: <DeliverySummary />,
    },
    {
      key: "stock-evolution",
      label: "Stock Evolution",
      children: <StockEvolution />,
    },
    {
      key: "stocks",
      label: "Stocks",
      children: <StockData />,
    },
  ];

  // Combine base tabs with dynamic tabs
  const allTabs = [...baseTabs, ...dynamicTabs];

  const handleTabChange = (key) => {
    // Set active key with a slight delay to ensure proper rendering
    setTimeout(() => {
      setActiveKey(key);
    }, 0);

    // Check if it's a base tab or dynamic tab
    const baseTabKeys = baseTabs.map((tab) => tab.key);
    if (baseTabKeys.includes(key)) {
      // For base tabs, update URL directly
      navigate(`/products/${key}`);
    } else {
      // For dynamic tabs, find the tab and update URL accordingly
      const dynamicTab = dynamicTabs.find((tab) => tab.key === key);
      if (dynamicTab && dynamicTab.key) {
        // Extract tab type and options from the key
        const keyParts = dynamicTab.key.split("-");
        const tabType = keyParts[0];

        // Find the tab data to get options
        const tabData = dynamicTab.children.props.tabData;
        if (tabData) {
          updateUrlForTab(tabData.type, {
            clientId: tabData.clientId,
            productId: tabData.productId,
            variationId: tabData.variationId,
            warehouseId: tabData.warehouseId,
          });
        }
      }
    }
  };

  return (
    <div className="page-wrapper">
      <div className="p-1">
        <Tabs
          activeKey={activeKey}
          items={allTabs}
          onChange={handleTabChange}
          type="card"
          size="small"
        />
      </div>
    </div>
  );
};

export default ProductDashboard;
